import { DataFlowDSLAdapterContext, type IDataFlowDSLModelAdapter, stringIsNullOrEmpty } from 'shell-models-lib';
import { type DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { type DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { type DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';

export class BaseFormatAdapter<T extends DataFlowDatasetSettings> implements IDataFlowDSLModelAdapter<T> {
    protected context: DataFlowDSLAdapterContext;

    public initContext(context: DataFlowDSLAdapterContext): void {
        this.context = context;
    }

    public async toDSL(setting: T, ...other: unknown[]): Promise<string[]> {
        return other && other[0] ? this.toSourceDSL(setting) : this.toSinkDSL(setting);
    }

    public async toModel(configs: Map<string, string>, ...other: unknown[]): Promise<T> {
        return other && other[0]
            ? this.toSourceModel(other[1] as DataFlowSource, configs, other[2] as string[])
            : this.toSinkModel(other[1] as DataFlowSink, configs, other[2] as string[]);
    }

    protected async toSourceDSL(settings: T): Promise<string[]> {
        return [];
    }

    protected async toSinkDSL(settings: T): Promise<string[]> {
        return [];
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<T> {
        return undefined;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<T> {
        return undefined;
    }

    protected _additionalConfigsToDSL(settings: T): string {
        const EOL = (n: number = 1) => `\n${'\t'.repeat(n)}`;
        if (settings?.additionalConfigs?.length) {
            return `additionalConfigs: [${EOL(2)}${settings.additionalConfigs
                .filter(
                    (config) =>
                        !stringIsNullOrEmpty(config.key.expression) && !stringIsNullOrEmpty(config.value.expression)
                )
                .map((config) => DataFlowExpressionUtils.wrapExpressionMapConfigValue(config))
                .join(`,${EOL(2)}`)}${EOL()}]`;
        }
        return undefined;
    }

    protected _additionalConfigsToModel(
        expression: string,
        owner: DataFlowSource
    ): Array<Record<'key' | 'value', DataFlowExpression>> {
        return DataFlowExpressionUtils.parseScopeArrayInDsl(expression)
            .map((config) => DataFlowExpressionUtils.parseStringMapInDsl(config))
            .map((configs) => {
                const key = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'key'`), owner);
                const value = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get(`'value'`), owner);
                return {
                    key,
                    value
                };
            });
    }
}
